/** Shapes returned by the m28-education and m29-tertiary-tvet APIs (fields the portal uses). */

/** Generic cursor-page envelope shared by every list endpoint in both modules. */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

// ─────────────────────────────────────────────────────────────────────────
// m28-education
// ─────────────────────────────────────────────────────────────────────────

export type SchoolLevel = 'PRIMARY' | 'SECONDARY' | 'COMBINED';
export const SCHOOL_LEVELS: SchoolLevel[] = ['PRIMARY', 'SECONDARY', 'COMBINED'];

export type SchoolSector = 'GOVERNMENT' | 'GRANT_AIDED' | 'PRIVATE';
export const SCHOOL_SECTORS: SchoolSector[] = ['GOVERNMENT', 'GRANT_AIDED', 'PRIVATE'];

export interface School {
  id: string;
  name: string;
  district: string;
  level: SchoolLevel;
  sector: SchoolSector;
  address: string;
  capacity: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  deletedAt?: string | null;
}

export type GuardianRelation = 'MOTHER' | 'FATHER' | 'GUARDIAN' | 'OTHER';

export interface Guardian {
  nin: string;
  relation: GuardianRelation;
  primaryContact: boolean;
}

export type StudentStatus = 'ACTIVE' | 'TRANSFERRED' | 'GRADUATED' | 'WITHDRAWN';
export const STUDENT_STATUSES: StudentStatus[] = ['ACTIVE', 'TRANSFERRED', 'GRADUATED', 'WITHDRAWN'];

export interface StudentRecord {
  id: string;
  nin: string;
  fullName: string;
  dob: string;
  gender: 'M' | 'F';
  currentSchoolId: string | null;
  yearGroup: string;
  guardians: Guardian[];
  status: StudentStatus;
  /** Stripped unless the caller is staff or the student's own declared guardian. */
  senFlags?: string[];
  createdAt: string;
  updatedAt: string;
}

export type AdmissionIntake = 'P1' | 'S1' | 'TRANSFER';
export const ADMISSION_INTAKES: AdmissionIntake[] = ['P1', 'S1', 'TRANSFER'];

export type CatchmentSource = 'SELF_DECLARED' | 'GEOSERVE_PARCEL';

export interface AdmissionPreference {
  schoolId: string;
  rank: number;
}

export type AdmissionApplicationState = 'SUBMITTED' | 'OFFERED' | 'WAITLISTED' | 'ACCEPTED' | 'DECLINED' | 'WITHDRAWN';
export const ADMISSION_APPLICATION_STATES: AdmissionApplicationState[] = ['SUBMITTED', 'OFFERED', 'WAITLISTED', 'ACCEPTED', 'DECLINED', 'WITHDRAWN'];

export interface ScoreBreakdown {
  total?: number;
  lotteryRank?: number;
  catchment?: number;
  sibling?: number;
  specialNeeds?: number;
  [key: string]: unknown;
}

export interface AdmissionApplication {
  id: string;
  studentNin: string;
  studentFullName: string;
  studentDob: string;
  guardianNin: string;
  intake: AdmissionIntake;
  requestedYearGroup: string | null;
  preferences: AdmissionPreference[];
  declaredDistrict: string;
  resolvedDistrict: string;
  residentialParcelNo: string | null;
  catchmentSource: CatchmentSource;
  siblingAtSchoolId: string | null;
  specialNeeds: string[];
  roundId: string | null;
  scoreBreakdown: ScoreBreakdown | null;
  offeredSchoolId: string | null;
  offeredPreferenceRank: number | null;
  waitlistSchoolId: string | null;
  state: AdmissionApplicationState;
  decidedAt: string | null;
  studentRecordId: string | null;
  createdAt: string;
  updatedAt: string;
  waitlistPosition?: number | null;
}

export type AdmissionRoundStatus = 'DRAFT' | 'OPEN' | 'RUN' | 'CLOSED';
export const ADMISSION_ROUND_STATUSES: AdmissionRoundStatus[] = ['DRAFT', 'OPEN', 'RUN', 'CLOSED'];

export interface AdmissionRound {
  id: string;
  intake: AdmissionIntake;
  schoolIds: string[];
  status: AdmissionRoundStatus;
  capacitySnapshot: Record<string, number>;
  openedAt: string | null;
  ranAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Only present on the POST .../run response. */
  offers?: number;
  waitlisted?: number;
}

export interface AdmissionsCriterion {
  code: string;
  label: string;
  points?: number;
  note: string;
}

export interface AdmissionsCriteria {
  maxScore: number;
  criteria: AdmissionsCriterion[];
  algorithm: string;
}

export type ExamBoard = 'IGCSE' | 'A_LEVEL' | 'TVET' | 'NATIONAL';
export const EXAM_BOARDS: ExamBoard[] = ['IGCSE', 'A_LEVEL', 'TVET', 'NATIONAL'];

export type ExamRegistrationState = 'REGISTERED' | 'CONFIRMED' | 'WITHDRAWN';
export const EXAM_REGISTRATION_STATES: ExamRegistrationState[] = ['REGISTERED', 'CONFIRMED', 'WITHDRAWN'];

export interface ExamRegistration {
  id: string;
  candidateNin: string;
  board: ExamBoard;
  subjects: string[];
  centre: string;
  feeAmount: number | null;
  payIntentRefNo: string | null;
  state: ExamRegistrationState;
  registeredAt: string;
  confirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ExamGrade = 'A*' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'U';
export const EXAM_GRADES: ExamGrade[] = ['A*', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'U'];

export interface SubjectResult {
  subjectCode: string;
  grade: ExamGrade;
}

export interface ResultRecord {
  id: string;
  examRegistrationId: string;
  candidateNin: string;
  board: ExamBoard;
  subjectResults: SubjectResult[];
  certSerial: string;
  certQrToken: string;
  certIssuedAt: string;
  revokedAt: string | null;
  revokeReason: string | null;
  releasedAt: string;
  releasedBy: string;
}

export interface ReleaseResultsResponse {
  released: string[];
  skipped: Array<{ candidateNin: string; reason: string }>;
}

export type ScholarshipState = 'ACADEMIC_SCORED' | 'UNDER_REVIEW' | 'SCORED' | 'APPROVED' | 'REJECTED' | 'BOND_ACCEPTED' | 'DECLINED';
export const SCHOLARSHIP_STATES: ScholarshipState[] = ['ACADEMIC_SCORED', 'UNDER_REVIEW', 'SCORED', 'APPROVED', 'REJECTED', 'BOND_ACCEPTED', 'DECLINED'];

export interface ScholarshipApplication {
  id: string;
  applicantNin: string;
  scheme: string;
  programme: string;
  institution: string;
  academicScore: number | null;
  criteriaScore: number | null;
  totalScore: number | null;
  bondYears: number | null;
  bondAmount: number | null;
  state: ScholarshipState;
  decisionNotes: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
  rank?: number | null;
}

export interface ScholarshipCriteria {
  scheme: string;
  maxTotalScore: number;
  academic: { maxScore: number; note: string; gradePoints: Record<string, number> };
  criteria: { maxScore: number; note: string };
}

export interface QualificationEntry {
  id: string;
  credential: string;
  institution: string;
  year: number;
  verified: boolean;
  verifiedBy: string | null;
  verifiedAt: string | null;
}

export type TeacherStatus = 'PENDING_VERIFICATION' | 'REGISTERED' | 'SUSPENDED' | 'REVOKED';
export const TEACHER_STATUSES: TeacherStatus[] = ['PENDING_VERIFICATION', 'REGISTERED', 'SUSPENDED', 'REVOKED'];

export interface TeacherRegister {
  id: string;
  nin: string;
  fullName: string;
  qualifications: QualificationEntry[];
  subjectSpecialisms: string[];
  status: TeacherStatus;
  currentSchoolId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type InspectionOutcome = 'PASS' | 'FAIL' | 'CONDITIONAL';

export interface Inspection {
  id: string;
  licenceId: string;
  scheduledAt: string;
  inspectedAt: string | null;
  inspectorSubject: string | null;
  outcome: InspectionOutcome | null;
  notes: string | null;
  createdAt: string;
}

export type LicenceState = 'SUBMITTED' | 'INSPECTION_SCHEDULED' | 'INSPECTED' | 'APPROVED' | 'REJECTED' | 'SUSPENDED' | 'REVOKED';
export const LICENCE_STATES: LicenceState[] = ['SUBMITTED', 'INSPECTION_SCHEDULED', 'INSPECTED', 'APPROVED', 'REJECTED', 'SUSPENDED', 'REVOKED'];

export interface PrivateSchoolLicence {
  id: string;
  applicantNin: string;
  proposedSchoolName: string;
  district: string;
  level: SchoolLevel;
  state: LicenceState;
  licenceNo: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  schoolId: string | null;
  rejectionReason: string | null;
  inspections?: Inspection[];
  createdAt: string;
  updatedAt: string;
}

export interface VerifyQualificationResult {
  nin: string;
  found: boolean;
  qualifications: Array<{ board: ExamBoard; subjectResults: SubjectResult[]; certSerial: string; certValid: boolean; issuedAt: string }>;
}

// ─────────────────────────────────────────────────────────────────────────
// m29-tertiary-tvet
// ─────────────────────────────────────────────────────────────────────────

export type ProfessionStatus = 'ACTIVE' | 'RETIRED';

export interface Profession {
  id: string;
  code: string;
  name: string;
  licenceClasses: string[];
  cpdCycleMonths: number;
  cpdRequiredPoints: number;
  renewalPeriodMonths: number;
  registrationFeeAmount: number | null;
  renewalFeeAmount: number | null;
  status: ProfessionStatus;
  createdAt: string;
  updatedAt: string;
}

export interface QualificationEvidenceEntry {
  credential: string;
  institution: string;
  year: number;
}

export type RegistrantStatus = 'PENDING_VERIFICATION' | 'REGISTERED' | 'SUSPENDED' | 'REVOKED' | 'LAPSED';
export const REGISTRANT_STATUSES: RegistrantStatus[] = ['PENDING_VERIFICATION', 'REGISTERED', 'SUSPENDED', 'REVOKED', 'LAPSED'];

export interface ProfessionalRegistrant {
  id: string;
  professionId: string;
  professionCode: string;
  professionName: string;
  registrantNin: string;
  fullName: string;
  licenceClass: string;
  licenceNo: string | null;
  qualificationEvidence: QualificationEvidenceEntry[];
  status: RegistrantStatus;
  feePaid: boolean;
  payIntentRefNo: string | null;
  renewalPayIntentRefNo: string | null;
  registeredAt: string | null;
  expiresAt: string | null;
  statusReason: string | null;
  statusChangedBy: string | null;
  statusChangedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CpdEntry {
  id: string;
  registrantId: string;
  activity: string;
  points: number;
  evidenceDocId: string | null;
  loggedAt: string;
  auditSampled: boolean;
  sampledBy: string | null;
  sampledAt: string | null;
  auditOutcome: 'PASS' | 'FAIL' | null;
  auditNotes: string | null;
  createdAt: string;
}

export interface PublicRegistrantEntry {
  professionCode: string;
  professionName: string;
  fullName: string;
  licenceClass: string;
  licenceNo: string | null;
  status: RegistrantStatus;
  expiresAt: string | null;
}

export interface PublicLicenceLookupResult {
  found: boolean;
  professionCode?: string;
  professionName?: string;
  fullName?: string;
  licenceClass?: string;
  licenceNo?: string | null;
  status?: RegistrantStatus;
  expiresAt?: string | null;
}

export type RecognitionState = 'SUBMITTED' | 'UNDER_REVIEW' | 'DETERMINED' | 'CERTIFIED' | 'WITHDRAWN';
export const RECOGNITION_STATES: RecognitionState[] = ['SUBMITTED', 'UNDER_REVIEW', 'DETERMINED', 'CERTIFIED', 'WITHDRAWN'];

export type EquivalenceOutcome = 'RECOGNIZED' | 'PARTIALLY_RECOGNIZED' | 'NOT_RECOGNIZED';
export const EQUIVALENCE_OUTCOMES: EquivalenceOutcome[] = ['RECOGNIZED', 'PARTIALLY_RECOGNIZED', 'NOT_RECOGNIZED'];

export type VerificationMethod = 'EMAIL' | 'PHONE' | 'PORTAL' | 'POSTAL';
export const VERIFICATION_METHODS: VerificationMethod[] = ['EMAIL', 'PHONE', 'PORTAL', 'POSTAL'];

export type VerificationStepOutcome = 'PENDING' | 'CONFIRMED' | 'DISCREPANCY' | 'NO_RESPONSE';

export interface RecognitionVerificationStep {
  id: string;
  recognitionApplicationId: string;
  method: VerificationMethod;
  institutionContact: string | null;
  notes: string | null;
  outcome: VerificationStepOutcome;
  respondedAt: string | null;
  createdAt: string;
}

export interface RecognitionApplication {
  id: string;
  applicantNin: string;
  applicantFullName: string;
  awardTitle: string;
  awardingInstitution: string;
  awardingCountry: string;
  awardYear: number | null;
  transcriptsDocIds: string[];
  feeAmount: number | null;
  payIntentRefNo: string | null;
  state: RecognitionState;
  equivalenceOutcome: EquivalenceOutcome | null;
  equivalenceLevel: string | null;
  equivalenceNotes: string | null;
  determinedBy: string | null;
  determinedAt: string | null;
  certSerial: string | null;
  certQrToken: string | null;
  certIssuedAt: string | null;
  certRevokedAt: string | null;
  certRevokeReason: string | null;
  withdrawnAt: string | null;
  createdAt: string;
  updatedAt: string;
  verificationSteps?: RecognitionVerificationStep[];
}

export type ApprenticeshipState = 'ACTIVE' | 'SUSPENDED' | 'TERMINATED' | 'COMPLETED';
export const APPRENTICESHIP_STATES: ApprenticeshipState[] = ['ACTIVE', 'SUSPENDED', 'TERMINATED', 'COMPLETED'];

export type EntryQualificationSource = 'M28' | 'NONE';

export interface Apprenticeship {
  id: string;
  employerId: string;
  employerName: string;
  apprenticeNin: string;
  apprenticeName: string;
  trade: string;
  supervisorNin: string;
  supervisorName: string;
  startDate: string;
  expectedEndDate: string;
  state: ApprenticeshipState;
  entryQualificationSnapshot: unknown;
  entryQualificationSource: EntryQualificationSource | null;
  entryQualificationCheckedAt: string | null;
  completionCertSerial: string | null;
  completionCertQrToken: string | null;
  completionCertIssuedAt: string | null;
  terminatedReason: string | null;
  terminatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CountersignStatus = 'PENDING' | 'COUNTERSIGNED' | 'DISPUTED';

export interface ApprenticeshipLogEntry {
  id: string;
  apprenticeshipId: string;
  entryDate: string;
  hoursLogged: number | null;
  tasksPerformed: string;
  countersignStatus: CountersignStatus;
  countersignedBy: string | null;
  countersignedAt: string | null;
  disputeReason: string | null;
  createdAt: string;
}

export type AccreditationInstitutionType = 'UNIVERSITY' | 'TVET_COLLEGE' | 'PRIVATE_TERTIARY';
export const ACCREDITATION_INSTITUTION_TYPES: AccreditationInstitutionType[] = ['UNIVERSITY', 'TVET_COLLEGE', 'PRIVATE_TERTIARY'];

export type ProgrammeLevel = 'CERTIFICATE' | 'DIPLOMA' | 'BACHELOR' | 'MASTER' | 'DOCTORATE' | 'TVET_LEVEL_1' | 'TVET_LEVEL_2' | 'TVET_LEVEL_3' | 'TVET_LEVEL_4' | 'TVET_LEVEL_5';
export const PROGRAMME_LEVELS: ProgrammeLevel[] = [
  'CERTIFICATE',
  'DIPLOMA',
  'BACHELOR',
  'MASTER',
  'DOCTORATE',
  'TVET_LEVEL_1',
  'TVET_LEVEL_2',
  'TVET_LEVEL_3',
  'TVET_LEVEL_4',
  'TVET_LEVEL_5',
];

export type ProgrammeStatus = 'PENDING' | 'ACTIVE' | 'WITHDRAWN';

export interface AccreditedProgramme {
  id: string;
  programmeName: string;
  level: ProgrammeLevel;
  nqfLevel: number | null;
  status: ProgrammeStatus;
}

export type AccreditationState =
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'SITE_VISIT_SCHEDULED'
  | 'SITE_VISIT_COMPLETED'
  | 'ACCREDITED'
  | 'REJECTED'
  | 'SUSPENDED'
  | 'REVOKED';
export const ACCREDITATION_STATES: AccreditationState[] = [
  'SUBMITTED',
  'UNDER_REVIEW',
  'SITE_VISIT_SCHEDULED',
  'SITE_VISIT_COMPLETED',
  'ACCREDITED',
  'REJECTED',
  'SUSPENDED',
  'REVOKED',
];

export interface InstitutionAccreditation {
  id: string;
  institutionName: string;
  institutionType: AccreditationInstitutionType;
  applicantSubject: string;
  feeAmount: number | null;
  payIntentRefNo: string | null;
  feePaid: boolean;
  state: AccreditationState;
  reviewCycleYears: number;
  siteVisitScheduledAt: string | null;
  siteVisitCompletedAt: string | null;
  siteVisitNotes: string | null;
  decisionNotes: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  accreditationNo: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  nextReviewDueAt: string | null;
  certSerial: string | null;
  certQrToken: string | null;
  certRevokedAt: string | null;
  certRevokeReason: string | null;
  programmes: AccreditedProgramme[];
  createdAt: string;
  updatedAt: string;
}

export interface VerifyRecognitionResult {
  found: boolean;
  valid?: boolean;
  awardTitle?: string;
  awardingCountry?: string;
  equivalenceOutcome?: EquivalenceOutcome | null;
  equivalenceLevel?: string | null;
  issuedAt?: string | null;
  revoked?: boolean;
}

export interface VerifyProfessionalResult {
  found: boolean;
  valid?: boolean;
  professionCode?: string;
  professionName?: string;
  registrantNin?: string;
  fullName?: string;
  licenceClass?: string;
  status?: RegistrantStatus;
  expiresAt?: string | null;
}
